import { useState, useCallback, useMemo } from 'react';
import { Head } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { PackCard } from '../../components/packs/PackCard';
import { EmptyState } from '../../components/packs/EmptyState';
import { Toast } from '../../components/packs/Toast';
import { useCartPacks } from '../../hooks/useCartPacks';
import { useAddPack } from '../../hooks/useAddPack';
import { route } from '../../utils/route';
import type { ProductPack } from '../../types/packs';

const SUBTITLES = [
  'Soluciones pensadas para tu negocio gastronómico.',
  'Productos combinados para simplificar tu operación.',
  'Packs diseñados para resolver tus necesidades.',
  'Todo lo que necesitás, listo para pedir.',
];

interface SolutionsProps {
  packs: ProductPack[];
  appUrl: string;
}

export default function Solutions({ packs, appUrl }: SolutionsProps) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const subtitle = useMemo(() => SUBTITLES[Math.floor(Math.random() * SUBTITLES.length)], []);

  const { isPackInCart, addPackTracking, removePackTracking, refreshCart } = useCartPacks();

  const showSuccessToast = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
  }, []);

  const { addPack, removePack, loadingPackId } = useAddPack({
    onSuccess: (pack, itemKeys) => {
      addPackTracking(pack, itemKeys);
      showSuccessToast(`"${pack.title}" agregado al carrito`);
    },
    onRemoveSuccess: (packId) => {
      removePackTracking(packId);
      showSuccessToast('Solución removida del carrito');
    },
    refreshCart,
    source: 'solution',
  });

  const handleAdd = useCallback(async (pack: ProductPack) => {
    try { await addPack(pack); } catch {}
  }, [addPack]);

  const handleRemove = useCallback(async (pack: ProductPack) => {
    try { await removePack(pack); } catch {}
  }, [removePack]);

  const pageDescription = 'Descubrí nuestras soluciones gastronómicas. Packs pensados para Airbnbs, oficinas, eventos y más. Productos frescos listos para pedir con delivery.';
  const pageUrl = `${appUrl}${route('solutions.index')}`;

  const listSchema = useMemo(() => JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Soluciones Gastronómicas - Abasto Virtual",
    "description": pageDescription,
    "numberOfItems": packs.length,
    "itemListElement": packs.map((pack, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `${appUrl}${route('solutions.show', { id: pack.slug || pack.id })}`,
      "name": pack.title,
    })),
  }), [packs, appUrl, pageDescription]);

  return (
    <MainLayout>
      <Head>
        <title>Soluciones Gastronómicas - Abasto Virtual</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Soluciones Gastronómicas - Abasto Virtual" />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Soluciones Gastronómicas - Abasto Virtual" />
        <meta property="twitter:description" content={pageDescription} />
        <script type="application/ld+json">{listSchema}</script>
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {packs.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <PageHeader
              title="Soluciones Gastronómicas"
              subtitle={`${subtitle} Agregá todo a tu carrito con un solo click.`}
            />

            {/* Disclaimers */}
            <div className="mb-6 flex flex-col gap-2">
              <p className="m-0 flex items-start gap-1.5 text-[13px] leading-snug text-gray-400">
                <i className="fa fa-info-circle mt-0.5 flex-shrink-0 text-xs" />
                Las imágenes son ilustrativas y generadas con IA. Los productos y el packaging pueden variar.
              </p>
              <p className="m-0 flex items-start gap-1.5 text-[13px] leading-snug text-gray-400">
                <i className="fa fa-shopping-cart mt-0.5 flex-shrink-0 text-xs" />
                Una vez agregado, podés modificar cantidades o eliminar productos del carrito de forma individual.
              </p>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
              {packs.map((pack) => (
                <PackCard
                  key={pack.id}
                  pack={pack}
                  isInCart={isPackInCart(pack)}
                  isLoading={loadingPackId === pack.id}
                  onAdd={() => handleAdd(pack)}
                  onRemove={() => handleRemove(pack)}
                  detailRoute="solutions.show"
                />
              ))}
            </div>
          </>
        )}

        {showToast && (
          <Toast message={toastMessage} onClose={() => setShowToast(false)} />
        )}
      </div>
    </MainLayout>
  );
}
